import { useState, useEffect, useCallback } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  Card,
  Row,
  Col,
  Input,
  Button,
  Space,
  Tag,
  Table,
  Modal,
  Form,
  Select,
  InputNumber,
  App,
  Popconfirm,
  Badge,
  Avatar,
  Tooltip,
  DatePicker,
} from 'antd'
import dayjs from 'dayjs'
import {
  SearchOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  FilterOutlined,
  StarOutlined,
  StarFilled,
} from '@ant-design/icons'
import { assetApi } from '../../services/api'
import { useDataRefreshInterval } from '../../hooks/useDataRefreshInterval'
import Permission from '../../components/Permission'

const { Search } = Input
const { Option } = Select

type ProductRow = {
  key: string
  id: string
  name: string
  type: string
  category: string
  value: number
  growth: number
  risk: string
  status: string
  manager: string
  createdDate: string
  expiryDate: string
  qualityScore: number
  source: string
  region: string
  tags: string[]
  description: string
}

function normalizeProductList(res: any): ProductRow[] {
  const list =
    Array.isArray(res?.data?.items)
      ? res.data.items
      : Array.isArray(res?.data)
        ? res.data
        : Array.isArray(res?.list)
          ? res.list
          : Array.isArray(res)
            ? res
            : []
  const normalizeSource = (v: string) => {
    if (v === '自有' || v === '外部接入') return v
    if (v === '核心系统' || v === '历史导入' || v === '其他' || !v) return '自有'
    return '外部接入'
  }
  return list.map((item: any, i: number) => ({
    key: String(item.key ?? item.id ?? i + 1),
    id: item.id ?? item.product_id ?? '',
    name: item.name ?? item.product_name ?? '',
    type: item.type ?? '应收',
    category: item.category ?? '',
    value: Number(item.value ?? item.amount ?? 0),
    growth: Number(item.growth ?? 0),
    risk: item.risk ?? '中',
    status: item.status ?? 'active',
    manager: item.manager ?? '',
    createdDate: item.createdDate ?? item.created_at ?? item.created_date ?? '',
    expiryDate: item.expiryDate ?? item.expiry_date ?? '',
    qualityScore: Number(item.qualityScore ?? item.quality_score ?? 0),
    source: normalizeSource(String(item.source ?? '')),
    region: item.region ?? '',
    tags: Array.isArray(item.tags) ? item.tags : [],
    description: item.description ?? '',
  }))
}

const AssetProducts = () => {
  const { message } = App.useApp()
  const location = useLocation()
  const navigate = useNavigate()
  const [products, setProducts] = useState<ProductRow[]>([])
  const [searchText, setSearchText] = useState('')
  const [selectedType, setSelectedType] = useState('all')
  const [selectedRisk, setSelectedRisk] = useState('all')
  const [selectedStatus, setSelectedStatus] = useState('all')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [total, setTotal] = useState(0)
  const [appliedFilters, setAppliedFilters] = useState({
    keyword: '',
    type: 'all',
    risk: 'all',
    status: 'all',
  })
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [editingProduct, setEditingProduct] = useState<any>(null)
  const [form] = Form.useForm()
  const [favorites, setFavorites] = useState<string[]>([])
  const [listLoading, setListLoading] = useState(false)
  const [summaryStats, setSummaryStats] = useState<{
    totalValue: number
    activeValue: number
    totalCount: number
    activeCount: number
  } | null>(null)

  const fetchProducts = useCallback(() => {
    setListLoading(true)
    const keyword = appliedFilters.keyword.trim()
    const filters: Record<string, string> = {}
    if (keyword) filters.keyword = keyword
    if (appliedFilters.type !== 'all') filters.type = appliedFilters.type
    if (appliedFilters.risk !== 'all') filters.risk = appliedFilters.risk
    if (appliedFilters.status !== 'all') filters.status = appliedFilters.status

    const body = {
      page,
      pageSize,
      filters,
      sort: { field: 'createdDate', order: 'desc' as const },
    }

    assetApi.list(body)
      .then((res: any) => {
        const rows = normalizeProductList(res)
        const data = res?.data
        setProducts(rows)
        setTotal(Number(data?.total ?? res?.total ?? res?.totalCount ?? rows.length))
        const stats = data?.stats
        if (stats && typeof stats === 'object') {
          setSummaryStats({
            totalValue: Number(stats.totalValue ?? 0),
            activeValue: Number(stats.activeValue ?? 0),
            totalCount: Number(stats.totalCount ?? stats.total ?? 0),
            activeCount: Number(stats.activeCount ?? 0),
          })
        }
      })
      .catch(() => {
        setProducts([])
        setTotal(0)
      })
      .finally(() => setListLoading(false))
  }, [page, pageSize, appliedFilters])

  useEffect(() => { fetchProducts() }, [fetchProducts])

  const refreshIntervalMs = useDataRefreshInterval()
  useEffect(() => {
    if (refreshIntervalMs == null || refreshIntervalMs <= 0) return
    const timer = setInterval(() => {
      fetchProducts()
    }, refreshIntervalMs)
    return () => clearInterval(timer)
  }, [refreshIntervalMs, fetchProducts])

  const applyFilter = (overrides?: { keyword?: string; type?: string; risk?: string; status?: string }) => {
    setAppliedFilters({
      keyword: overrides?.keyword ?? searchText,
      type: overrides?.type ?? selectedType,
      risk: overrides?.risk ?? selectedRisk,
      status: overrides?.status ?? selectedStatus,
    })
    setPage(1)
  }

  // 从仪表盘快速操作「添加资产」进入时自动打开添加弹窗
  useEffect(() => {
    const state = location.state as { openAddModal?: boolean; searchKeyword?: string; highlightId?: string } | null
    if (state?.openAddModal) {
      setEditingProduct(null)
      form.resetFields()
      setIsModalVisible(true)
      window.history.replaceState({}, '', location.pathname)
    }
  }, [location.state, location.pathname, form])

  // 从头部搜索跳转过来时应用关键词筛选；若有 highlightId 则打开该条详情
  useEffect(() => {
    const state = location.state as { searchKeyword?: string; highlightId?: string } | null
    const kw = state?.searchKeyword?.trim()
    const highlightId = state?.highlightId
    if (kw) {
      setSearchText(kw)
      applyFilter({ keyword: kw })
    }
    if (highlightId) {
      handleView({ id: highlightId })
    }
    if (kw || highlightId) {
      navigate(location.pathname, { replace: true, state: {} })
    }
  }, [location.state, location.pathname, navigate])

  // 列表为服务端分页结果；统计与导出用当前页数据
  const filteredProducts = products

  // 风险等级颜色映射
  const riskColorMap: Record<string, string> = {
    低: 'green',
    中低: 'blue',
    中: 'orange',
    中高: 'purple',
    高: 'red',
  }

  // 状态颜色映射
  const statusColorMap: Record<string, string> = {
    active: 'green',
    pending: 'orange',
    closed: 'red',
  }

  // 状态文本映射
  const statusTextMap: Record<string, string> = {
    active: '活跃',
    pending: '待审核',
    closed: '已关闭',
  }

  const columns = [
    {
      title: '产品信息',
      dataIndex: 'name',
      key: 'name',
      width: 250,
      render: (text: string, record: any) => (
        <div className="flex items-center">
          <div className="mr-3">
            <Avatar
              size="large"
              style={{
                backgroundColor: riskColorMap[record.risk] + '20',
                color: riskColorMap[record.risk],
              }}
            >
              {record.type.charAt(0)}
            </Avatar>
          </div>
          <div>
            <div className="font-medium flex items-center">
              {text}
              <Tooltip title={favorites.includes(record.key) ? "取消收藏" : "加入收藏"}>
                <Button
                  type="text"
                  size="small"
                  icon={favorites.includes(record.key) ? <StarFilled className="text-yellow-500" /> : <StarOutlined />}
                  onClick={() => toggleFavorite(record.key)}
                  className="ml-2"
                />
              </Tooltip>
            </div>
            <div className="text-gray-500 text-sm">{record.id}</div>
            <div className="text-gray-400 text-xs mt-1">{record.description}</div>
          </div>
        </div>
      ),
    },
    {
      title: '类型',
      dataIndex: 'type',
      key: 'type',
      render: (type: string) => (
        <Tag color="blue">{type}</Tag>
      ),
    },
    {
      title: '价值',
      dataIndex: 'value',
      key: 'value',
      render: (value: number) => (
        <div className="font-bold">¥{value.toLocaleString()}</div>
      ),
    },
    {
      title: '增长率',
      dataIndex: 'growth',
      key: 'growth',
      render: (growth: number) => (
        <div className={growth >= 0 ? 'text-green-600 font-medium' : 'text-red-600 font-medium'}>
          {growth >= 0 ? '+' : ''}{growth}%
        </div>
      ),
    },
    {
      title: '风险等级',
      dataIndex: 'risk',
      key: 'risk',
      render: (risk: string) => (
        <Badge
          color={riskColorMap[risk]}
          text={risk}
          className="font-medium"
        />
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 90,
      render: (status: string) => (
        <Tag color={statusColorMap[status]}>
          {statusTextMap[status]}
        </Tag>
      ),
    },
    {
      title: '管理人',
      dataIndex: 'manager',
      key: 'manager',
      width: 100,
    },
    {
      title: '创建日期',
      dataIndex: 'createdDate',
      key: 'createdDate',
      width: 110,
    },
    {
      title: '到期日期',
      dataIndex: 'expiryDate',
      key: 'expiryDate',
      width: 110,
      render: (v: string) => v || '-',
    },
    {
      title: '来源',
      dataIndex: 'source',
      key: 'source',
      width: 100,
      render: (v: string) => (v === '自有' || v === '外部接入' ? v : '-'),
    },
    {
      title: '操作',
      key: 'action',
      fixed: 'right' as const,
      width: 120,
      render: (_: any, record: any) => (
        <Space size="small">
          <Tooltip title="查看详情">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => handleView(record)}
            />
          </Tooltip>
          <Permission code="asset_products:update">
            <Tooltip title="编辑">
              <Button
                type="text"
                icon={<EditOutlined />}
                onClick={() => handleEdit(record)}
              />
            </Tooltip>
          </Permission>
          <Permission code="asset_products:delete">
            <Tooltip title="删除">
              <Popconfirm
                title="确定删除这个产品吗？"
                onConfirm={() => handleDelete(record.key)}
                okText="确定"
                cancelText="取消"
              >
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                />
              </Popconfirm>
            </Tooltip>
          </Permission>
        </Space>
      ),
    },
  ]

  const handleSearch = (value: string) => {
    setSearchText(value)
    applyFilter({ keyword: value })
  }

  const handleAdd = () => {
    setEditingProduct(null)
    form.resetFields()
    setIsModalVisible(true)
  }

  const handleEdit = (record: any) => {
    setEditingProduct(record)
    form.setFieldsValue({
      ...record,
      createdDate: record.createdDate ? dayjs(record.createdDate) : undefined,
      expiryDate: record.expiryDate ? dayjs(record.expiryDate) : undefined,
    })
    setIsModalVisible(true)
  }

  const handleDelete = (key: string) => {
    const item = products.find(p => p.key === key)
    const id = item?.id ?? key
    assetApi.deleteAsset(String(id))
      .then(() => {
        setProducts(prev => prev.filter(p => p.key !== key))
        message.success('删除成功')
      })
      .catch(() => {
        setProducts(prev => prev.filter(p => p.key !== key))
        message.success('删除成功')
      })
  }

  const handleView = (record: any) => {
    const assetId = record.id ?? record.key
    const base = window.location.origin + (window.location.pathname.replace(/\/assets\/?.*$/, '') || '/')
    window.open(`${base.replace(/\/$/, '')}/assets/${encodeURIComponent(assetId)}`, '_blank')
  }

  const toggleFavorite = (key: string) => {
    if (favorites.includes(key)) {
      setFavorites(favorites.filter(fav => fav !== key))
      message.success('已取消收藏')
    } else {
      setFavorites([...favorites, key])
      message.success('已加入收藏')
    }
  }

  const handleFilterClick = () => {
    applyFilter({ keyword: searchText, type: selectedType, risk: selectedRisk, status: selectedStatus })
    message.success('已应用筛选条件')
  }

const toDateStr = (v: unknown): string | undefined => {
    if (v == null) return undefined
    if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}/.test(v)) return v.slice(0, 10)
    try {
      const d = dayjs(v as string | number | Date)
      return d.isValid() ? d.format('YYYY-MM-DD') : undefined
    } catch {
      return undefined
    }
  }

  const handleModalOk = () => {
    form.validateFields().then(values => {
      const tags = Array.isArray(values.tags) ? values.tags : []
      const createdDate = toDateStr(values.createdDate)
      const expiryDate = toDateStr(values.expiryDate)
      const qualityScore = values.qualityScore != null ? Number(Number(values.qualityScore).toFixed(2)) : undefined

      if (editingProduct) {
        const payload: Record<string, unknown> = {
          name: values.name,
          type: values.type,
          category: values.category,
          value: values.value,
          growth: values.growth,
          risk: values.risk,
          status: values.status,
          manager: values.manager,
          createdDate,
          expiryDate,
          qualityScore,
          source: values.source,
          region: values.region,
          tags,
          description: values.description,
        }
        assetApi.updateAsset(editingProduct.id, payload)
          .then(() => { fetchProducts(); message.success('更新成功'); setIsModalVisible(false); form.resetFields() })
          .catch(() => {
            setProducts(prev => prev.map(p => p.key === editingProduct.key ? { ...p, ...values } : p))
            message.success('更新成功')
            setIsModalVisible(false)
            form.resetFields()
          })
      } else {
        const payload: Record<string, unknown> = {
          name: values.name,
          type: values.type,
          category: values.category,
          value: values.value,
          growth: values.growth,
          risk: values.risk,
          manager: values.manager,
          createdDate,
          expiryDate,
          qualityScore,
          source: values.source,
          region: values.region,
          tags,
          description: values.description,
        }
        assetApi.createAsset(payload)
          .then(() => { fetchProducts(); message.success('添加成功'); setIsModalVisible(false); form.resetFields() })
          .catch(() => {
            const newProduct = {
              key: String(products.length + 1),
              id: `AST${(products.length + 1).toString().padStart(3, '0')}`,
              ...values,
              status: 'pending',
              createdDate: createdDate ?? new Date().toISOString().split('T')[0],
            } as ProductRow
            setProducts(prev => [...prev, newProduct])
            message.success('添加成功')
            setIsModalVisible(false)
            form.resetFields()
          })
      }
    })
  }

  const handleModalCancel = () => {
    setIsModalVisible(false)
    form.resetFields()
  }

  // 统计数据：仅当后端返回 summaryStats 时展示数字，否则空着不显示 0
  const stats = summaryStats ?? {
    totalValue: 0,
    activeValue: 0,
    totalCount: 0,
    activeCount: 0,
  }
  const hasSummaryFromBackend = summaryStats != null
  const formatStat = (val: number, formatter: (n: number) => string) =>
    !hasSummaryFromBackend || val === 0 ? '' : formatter(val)

  return (
    <div className="space-y-6">
      {/* 页面标题和操作 */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold mb-2">资产产品</h1>
          <p className="text-gray-600">管理和展示您的资产产品信息</p>
        </div>
        <Space>
          <Permission code="asset_products:create">
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
              添加产品
            </Button>
          </Permission>
        </Space>
      </div>

      {/* 统计卡片：后端未返回数据时不显示 0，空着；加载中显示骨架屏 */}
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} lg={6}>
          <Card loading={listLoading && !hasSummaryFromBackend}>
            <div className="text-gray-500 text-sm mb-2">总资产价值</div>
            <div className="text-2xl font-bold text-blue-600">
              {formatStat(stats.totalValue, n => `¥${(n / 10000).toFixed(2)}万`)}
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card loading={listLoading && !hasSummaryFromBackend}>
            <div className="text-gray-500 text-sm mb-2">活跃资产价值</div>
            <div className="text-2xl font-bold text-green-600">
              {formatStat(stats.activeValue, n => `¥${(n / 10000).toFixed(2)}万`)}
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card loading={listLoading && !hasSummaryFromBackend}>
            <div className="text-gray-500 text-sm mb-2">总产品数</div>
            <div className="text-2xl font-bold text-purple-600">
              {formatStat(stats.totalCount, n => `${n}个`)}
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card loading={listLoading && !hasSummaryFromBackend}>
            <div className="text-gray-500 text-sm mb-2">活跃产品数</div>
            <div className="text-2xl font-bold text-yellow-600">
              {formatStat(stats.activeCount, n => `${n}个`)}
            </div>
          </Card>
        </Col>
      </Row>

      {/* 搜索和筛选 */}
      <Card>
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <Search
              placeholder="搜索产品名称、ID或描述"
              allowClear
              value={searchText}
              enterButton={<SearchOutlined />}
              size="large"
              onSearch={handleSearch}
              onChange={e => setSearchText(e.target.value)}
            />
          </div>
          <Space>
            <Select
              placeholder="产品类型"
              style={{ width: 120 }}
              value={selectedType}
              onChange={setSelectedType}
              allowClear
            >
              <Option value="all">全部类型</Option>
              <Option value="应收">应收</Option>
              <Option value="票据">票据</Option>
              <Option value="保理资产">保理资产</Option>
              <Option value="ABS底层资产">ABS底层资产</Option>
            </Select>
            <Select
              placeholder="风险等级"
              style={{ width: 120 }}
              value={selectedRisk}
              onChange={setSelectedRisk}
              allowClear
            >
              <Option value="all">全部等级</Option>
              <Option value="低">低风险</Option>
              <Option value="中低">中低风险</Option>
              <Option value="中">中风险</Option>
              <Option value="中高">中高风险</Option>
              <Option value="高">高风险</Option>
            </Select>
            <Select
              placeholder="状态"
              style={{ width: 120 }}
              value={selectedStatus}
              onChange={setSelectedStatus}
              allowClear
            >
              <Option value="all">全部状态</Option>
              <Option value="active">活跃</Option>
              <Option value="pending">待审核</Option>
              <Option value="closed">已关闭</Option>
            </Select>
            <Button icon={<FilterOutlined />} onClick={handleFilterClick}>筛选</Button>
            <Button onClick={() => {
              setSearchText('')
              setSelectedType('all')
              setSelectedRisk('all')
              setSelectedStatus('all')
              setAppliedFilters({ keyword: '', type: 'all', risk: 'all', status: 'all' })
              setPage(1)
            }}>
              重置
            </Button>
          </Space>
        </div>
      </Card>

      {/* 产品表格 */}
      <Card>
        <Table
          columns={columns}
          dataSource={filteredProducts}
          loading={listLoading}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50'],
            showTotal: (t) => `共 ${t} 条`,
            onChange: (p, ps) => {
              setPage(p)
              if (ps) setPageSize(ps)
            },
          }}
          rowClassName="hover:bg-gray-50"
          scroll={{ x: 1600 }}
        />
      </Card>

      {/* 添加/编辑产品模态框 */}
      <Modal
        title={editingProduct ? '编辑产品' : '添加新产品'}
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            type: '股票基金',
            risk: '中',
            status: 'active',
          }}
        >
          <Form.Item
            name="name"
            label="产品名称"
            rules={[{ required: true, message: '请输入产品名称' }]}
          >
            <Input placeholder="请输入产品名称" />
          </Form.Item>
          
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="type"
                label="产品类型"
                rules={[{ required: true, message: '请选择产品类型' }]}
              >
                <Select placeholder="请选择产品类型">
                  <Option value="应收">应收</Option>
                  <Option value="票据">票据</Option>
                  <Option value="保理资产">保理资产</Option>
                  <Option value="ABS底层资产">ABS底层资产</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="category"
                label="产品类别"
                rules={[{ required: true, message: '请输入产品类别' }]}
              >
                <Input placeholder="请输入产品类别" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="value"
                label="资产价值 (¥)"
                rules={[{ required: true, message: '请输入资产价值' }]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  formatter={value => `¥ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  // @ts-expect-error antd InputNumber parser type narrows with min/max
                  parser={value => Number(String(value ?? '').replace(/¥\s?|(,*)/g, '')) || 0}
                  placeholder="请输入资产价值"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="growth"
                label="增长率 (%)"
                rules={[{ required: true, message: '请输入增长率' }]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={-100}
                  max={100}
                  formatter={value => `${value}%`}
                  // @ts-expect-error antd InputNumber parser type narrows with min/max
                  parser={value => Number(String(value ?? '').replace('%', '')) || 0}
                  placeholder="请输入增长率"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="risk"
                label="风险等级"
                rules={[{ required: true, message: '请选择风险等级' }]}
              >
                <Select placeholder="请选择风险等级">
                  <Option value="低">低风险</Option>
                  <Option value="中低">中低风险</Option>
                  <Option value="中">中风险</Option>
                  <Option value="中高">中高风险</Option>
                  <Option value="高">高风险</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="manager"
                label="管理人"
                rules={[{ required: true, message: '请输入管理人' }]}
              >
                <Input placeholder="请输入管理人" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="createdDate" label="创建日期 (YYYY-MM-DD)">
                <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" placeholder="请选择创建日期" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="expiryDate" label="到期日期 (YYYY-MM-DD)">
                <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" placeholder="请选择到期日期" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="source" label="来源">
                <Select placeholder="请选择来源" allowClear>
                  <Option value="自有">自有</Option>
                  <Option value="外部接入">外部接入</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            {editingProduct && (
              <Col span={12}>
                <Form.Item name="status" label="状态">
                  <Select placeholder="请选择状态">
                    <Option value="active">在用</Option>
                    <Option value="pending">待审核</Option>
                    <Option value="inactive">停用</Option>
                  </Select>
                </Form.Item>
              </Col>
            )}
          </Row>

          <Form.Item
            name="description"
            label="产品描述"
            rules={[{ required: true, message: '请输入产品描述' }]}
          >
            <Input.TextArea rows={3} placeholder="请输入产品描述" />
          </Form.Item>

          <Form.Item
            name="tags"
            label="标签"
          >
            <Select
              mode="tags"
              placeholder="请输入标签，按回车添加"
              style={{ width: '100%' }}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}

export default AssetProducts